import { useEffect, useState, useCallback } from 'react';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { ArrowUpFromLine, RefreshCw, CheckCircle2, XCircle, Clock, Building2 } from 'lucide-react';

const fmt = (n) => (Number(n) || 0).toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const STATUS_BADGE = {
    pending: <Badge className="bg-amber-100 text-amber-800 border-amber-300"><Clock className="h-3 w-3 mr-1" />Pendiente</Badge>,
    completed: <Badge className="bg-emerald-100 text-emerald-800 border-emerald-300"><CheckCircle2 className="h-3 w-3 mr-1" />Completado</Badge>,
    rejected: <Badge className="bg-red-100 text-red-800 border-red-300"><XCircle className="h-3 w-3 mr-1" />Rechazado</Badge>,
};

export default function AdminWithdrawals() {
    const [withdrawals, setWithdrawals] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filterStatus, setFilterStatus] = useState('pending');
    const [actionOpen, setActionOpen] = useState(false);
    const [selected, setSelected] = useState(null);
    const [adminNote, setAdminNote] = useState('');
    const [busy, setBusy] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await api.get('/admin/wallet/withdraw-requests', { params: filterStatus !== '__all__' ? { status: filterStatus } : {} });
            setWithdrawals(Array.isArray(data) ? data : []);
        } catch (e) { toast.error('Error al cargar solicitudes'); }
        finally { setLoading(false); }
    }, [filterStatus]);

    useEffect(() => { load(); }, [load]);

    const openAction = (req) => { setSelected(req); setAdminNote(''); setActionOpen(true); };

    const doAction = async (action) => {
        setBusy(true);
        try {
            await api.put(`/admin/wallet/withdraw-requests/${selected.id}/action`, { action, admin_note: adminNote });
            toast.success(action === 'approve' ? 'Retiro aprobado y marcado como completado' : 'Retiro rechazado y saldo reembolsado');
            setActionOpen(false);
            load();
        } catch (e) { toast.error(e?.response?.data?.detail || 'Error'); }
        finally { setBusy(false); }
    };

    const pendingCount = withdrawals.filter(w => w.status === 'pending').length;

    return (
        <div className="space-y-5" data-testid="admin-withdrawals">
            <div className="flex items-center gap-3 flex-wrap">
                <ArrowUpFromLine className="h-6 w-6 text-orange-600" />
                <h1 className="text-2xl font-semibold">Solicitudes de retiro OkWallet</h1>
                {pendingCount > 0 && <Badge className="bg-amber-100 text-amber-800">{pendingCount} pendientes</Badge>}
                <Button size="sm" variant="outline" onClick={load} className="ml-auto"><RefreshCw className="h-4 w-4 mr-1" />Actualizar</Button>
            </div>

            <div className="flex items-center gap-3">
                <Label className="text-sm shrink-0">Filtrar por estado:</Label>
                <Select value={filterStatus} onValueChange={setFilterStatus}>
                    <SelectTrigger className="w-48"><SelectValue /></SelectTrigger>
                    <SelectContent>
                        <SelectItem value="pending">Pendientes</SelectItem>
                        <SelectItem value="completed">Completadas</SelectItem>
                        <SelectItem value="rejected">Rechazadas</SelectItem>
                        <SelectItem value="__all__">Todas</SelectItem>
                    </SelectContent>
                </Select>
            </div>

            {loading ? (
                <div className="text-sm text-muted-foreground py-8 text-center">Cargando…</div>
            ) : withdrawals.length === 0 ? (
                <div className="rounded-2xl border border-dashed py-14 text-center text-sm text-muted-foreground">
                    No hay solicitudes con este estado.
                </div>
            ) : (
                <div className="space-y-3">
                    {withdrawals.map(w => (
                        <div key={w.id} className="rounded-xl border bg-card p-4 space-y-2" data-testid={`withdrawal-${w.id}`}>
                            <div className="flex items-center justify-between flex-wrap gap-2">
                                <div className="flex items-center gap-2">
                                    <ArrowUpFromLine className="h-5 w-5 text-orange-500" />
                                    <span className="font-semibold text-lg">${fmt(w.amount)} MXN</span>
                                    {STATUS_BADGE[w.status] || <Badge>{w.status}</Badge>}
                                </div>
                                <span className="text-xs text-muted-foreground">@{w.user_nickname} · {new Date(w.created_at).toLocaleString('es-MX')}</span>
                            </div>

                            <div className="rounded-lg border bg-secondary/30 p-2 text-sm flex items-start gap-2">
                                <Building2 className="h-4 w-4 text-blue-600 mt-0.5 shrink-0" />
                                <div>
                                    <div className="font-medium">{w.bank_account?.bank_name} · {w.bank_account?.account_holder}</div>
                                    <div className="text-xs font-mono text-muted-foreground">CLABE: {w.bank_account?.clabe}</div>
                                </div>
                            </div>

                            {w.notes && <p className="text-sm text-muted-foreground">Nota del usuario: {w.notes}</p>}
                            {w.admin_note && <p className="text-sm text-amber-700 bg-amber-50 rounded px-2 py-1">Respuesta admin: {w.admin_note}</p>}

                            {w.status === 'pending' && (
                                <div className="flex gap-2 pt-1">
                                    <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700" onClick={() => openAction(w)} data-testid={`approve-${w.id}`}>
                                        <CheckCircle2 className="h-4 w-4 mr-1" />Aprobar / Rechazar
                                    </Button>
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            )}

            {/* Action dialog */}
            <Dialog open={actionOpen} onOpenChange={setActionOpen}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Procesar solicitud de retiro</DialogTitle>
                    </DialogHeader>
                    {selected && (
                        <div className="space-y-4">
                            <div className="rounded-xl border p-3 space-y-1 bg-secondary/30">
                                <div className="font-semibold text-lg">${fmt(selected.amount)} MXN</div>
                                <div className="text-sm">Usuario: @{selected.user_nickname}</div>
                                <div className="text-sm">{selected.bank_account?.bank_name} · {selected.bank_account?.account_holder}</div>
                                <div className="text-xs font-mono text-muted-foreground">CLABE: {selected.bank_account?.clabe}</div>
                                {selected.notes && <div className="text-xs text-muted-foreground">Nota: {selected.notes}</div>}
                            </div>
                            <div>
                                <Label>Nota interna (aparece al usuario)</Label>
                                <Input value={adminNote} onChange={(e) => setAdminNote(e.target.value)} placeholder="Ej: Transferencia procesada por SPEI, referencia #123" />
                            </div>
                            <div className="rounded-lg bg-amber-50 border border-amber-200 p-2 text-xs text-amber-800">
                                <strong>Aprobar:</strong> Marca como completado. Realiza la transferencia SPEI manualmente antes de aprobar.<br />
                                <strong>Rechazar:</strong> El saldo se reembolsa automáticamente al usuario.
                            </div>
                        </div>
                    )}
                    <DialogFooter className="gap-2">
                        <Button variant="ghost" onClick={() => setActionOpen(false)}>Cancelar</Button>
                        <Button variant="outline" className="border-red-300 text-red-700 hover:bg-red-50" onClick={() => doAction('reject')} disabled={busy}>
                            <XCircle className="h-4 w-4 mr-1" />{busy ? '…' : 'Rechazar'}
                        </Button>
                        <Button className="bg-emerald-600 hover:bg-emerald-700" onClick={() => doAction('approve')} disabled={busy}>
                            <CheckCircle2 className="h-4 w-4 mr-1" />{busy ? '…' : 'Aprobar (ya transferí)'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
